import { Editor, Element, Transforms, Descendant, Range, Node, NodeEntry, Path } from 'slate';

import {
  MentionPersona,
  CustomElement,
  CustomElementTypes,
  CustomMarkTypes,
  LinkElement,
  MentionElement,
} from '@web/richText/types';

const CUSTOM_VOID_ELEMENTS = ['mention'];

export const toggleMark = (editor: Editor, format: CustomMarkTypes): void => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    removeMark(editor, format);
  } else {
    addMark(editor, format);
  }
};

export const addMark = (editor: Editor, format: CustomMarkTypes): void => {
  Editor.addMark(editor, format, true);
};

export const removeMark = (editor: Editor, format: CustomMarkTypes): void => {
  Editor.removeMark(editor, format);
};

const replaceWithBlock = (editor: Editor, format: CustomElementTypes): void => {
  if (format === 'mention') {
    return;
  }

  const el: CustomElement = { type: format, children: [] };

  const [...nodes] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && Element.isElement(n),
  });

  if (format === 'block-quote') {
    replaceWithBlockQuote(editor, nodes);
    return;
  }

  let nodeStart = null;
  nodes.forEach(([paragraph, path]) => {
    nodeStart = nodeStart || path;
    if (Element.isElement(paragraph)) {
      if (el.children.length) {
        el.children.push({ text: ' ' });
      }

      el.children.push(...paragraph.children);
    }
  });

  if (!el.children.length) {
    el.children.push({ text: '' });
  }

  insertNodesAt(editor, [el], nodeStart);
};

const replaceWithBlockQuote = (editor: Editor, nodes: NodeEntry<Node>[]): void => {
  const getNewElement = (): CustomElement => ({ type: 'block-quote', children: [] });

  const newNodes: Descendant[] = [getNewElement()];
  let nodeStart: Path = null;
  nodes.forEach(([paragraph, path]) => {
    nodeStart = nodeStart || path;
    const lastElement = newNodes[newNodes.length - 1];
    if (Element.isElement(paragraph)) {
      if (Element.isElement(lastElement) && lastElement.type !== 'block-quote') {
        newNodes.push(getNewElement());
      }

      const lastNode = newNodes[newNodes.length - 1];
      if (Element.isElement(lastNode) && Element.isElement(paragraph)) {
        if (lastNode.children.length) {
          lastNode.children.push({ text: '\n' });
        }

        lastNode.children.push(...paragraph.children);
      }
    }
  });

  insertNodesAt(editor, newNodes, nodeStart);
};

const insertNodesAt = (editor: Editor, newNodes: Descendant[], path: Path): void => {
  Transforms.removeNodes(editor);
  Transforms.insertNodes(editor, newNodes, { at: path });
};

export const toggleBlock = (editor: Editor, format: CustomElementTypes): void => {
  const isActive = isBlockActive(editor, format);
  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);

  if (!isActive && !isCollapsed) {
    replaceWithBlock(editor, format);
    return;
  }

  const newProperties: Partial<Descendant> = {
    type: isActive ? 'paragraph' : format,
  };

  if (isActive) {
    Transforms.setNodes(editor, newProperties, {
      match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === format,
    });
  } else {
    Transforms.setNodes(editor, newProperties);
  }
};

export const isBlockActive = (editor: Editor, format: CustomElementTypes): boolean => {
  try {
    const [match] = Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === format,
    });

    return !!match;
  } catch (error) {
    console.log(error);
    return false;
  }
};

export const isMarkActive = (editor: Editor, format: CustomMarkTypes): boolean => {
  try {
    const marks = Editor.marks(editor);

    return marks ? marks[format] === true : false;
  } catch (error) {
    console.log(error);
    return false;
  }
};

export const wrapLink = (editor: Editor, url: string): void => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link: LinkElement = {
    type: 'link',
    link: url,
    children: isCollapsed ? [{ text: url }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
    Transforms.move(editor, { unit: 'offset', distance: 1 });
  }
};

export const isLinkActive = (editor: Editor): boolean => {
  try {
    const [link] = Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
    });
    return !!link;
  } catch (error) {
    console.log(error);
    return false;
  }
};

export const unwrapLink = (editor: Editor): void => {
  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
  });
};

export const insertLink = (editor: Editor, url: string): void => {
  if (editor.selection) {
    removeMark(editor, 'highlight');
    wrapLink(editor, url);
    Transforms.move(editor);
  }
};

export const updateLinkUrl = (editor: Editor, url: string): void => {
  if (!url) {
    unwrapLink(editor);
    return;
  }

  Transforms.setNodes(
    editor,
    { link: url.trim() },
    {
      match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
    }
  );
};

export const insertMention = (editor: Editor, persona: MentionPersona): void => {
  const mention: MentionElement = {
    type: 'mention',
    displayName: persona.displayName,
    personaId: persona.id,
    children: [{ text: '' }],
  };

  const spaceAfterMention = {
    text: ' ',
  };

  Transforms.insertNodes(editor, [mention, spaceAfterMention]);
  Transforms.move(editor, { distance: 2 });
};

export const replacePlainUrls = (text: string): string => {
  // This regexp is to search for urls that are not enclosed in [] or ()
  const httpUrlRegexp =
    /(?:[([][^)]*[)\]])|((https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&//=]*))/g;
  const textWithUrlMarkdown = text.replace(httpUrlRegexp, (m, m1) => {
    return m1 ? `[${m1}](${m1})` : m;
  });

  return textWithUrlMarkdown;
};

export const isElementVoid = (element: Element): boolean => {
  return CUSTOM_VOID_ELEMENTS.includes(element.type);
};
